/**
 * CoinflowHostedCheckout Component
 * 
 * Uses Coinflow's hosted checkout link embedded in an iframe.
 * This redirects to Coinflow's hosted page for the checkout experience.
 * 3DS authentication is handled internally by Coinflow's hosted page.
 */

import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { apiClient } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { getDeviceId } from '@/lib/nsure';
import { useLogoSrc } from '@/hooks/useLogoSrc';

interface CoinflowHostedCheckoutProps {
  packageId?: string;
  scheduledOfferId?: string;
  amount: number; // in cents
  packageTitle: string;
  gcAmount?: number;
  scAmount?: number;
  gcBonusAmount?: number;
  scBonusAmount?: number;
  onSuccess: (paymentId: string, data: { isFirstPurchase?: boolean }) => void;
  onError: (error: Error) => void;
  onBack?: () => void;
  allowedPaymentMethods?: Array<'card' | 'googlePay' | 'applePay'>;
  /** Checkout session transaction ID - if provided, reuses this transaction instead of creating a new one */
  checkoutSessionId?: number | null;
  /** Promo code applied to this purchase */
  promoCode?: string;
  /** Promo bonus GC amount (calculated during checkout) */
  promoBonusGC?: number;
  /** Promo bonus SC amount (calculated during checkout) */
  promoBonusSC?: number;
}

export function CoinflowHostedCheckout({
  packageId,
  scheduledOfferId,
  amount,
  packageTitle,
  onSuccess,
  onError,
  onBack,
  allowedPaymentMethods = ['card', 'googlePay', 'applePay'], // Card, Google Pay, Apple Pay only (no ACH)
  checkoutSessionId,
  promoCode,
  promoBonusGC,
  promoBonusSC,
}: CoinflowHostedCheckoutProps) {
  const [checkoutLink, setCheckoutLink] = useState<string | null>(null);
  const [transactionId, setTransactionId] = useState<number | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFirstPurchase, setIsFirstPurchase] = useState<boolean>(false);
  
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // Track if we've already initiated checkout to prevent duplicate calls
  const hasInitiatedRef = useRef(false);
  // Track if payment completed successfully (to avoid marking as abandoned)
  const paymentCompletedRef = useRef(false);
  // Track if this component created its own transaction (not using shared checkout session)
  const ownsTransactionRef = useRef(false);

  const logoSrc = useLogoSrc();
  
  // Get deviceId from nSure SDK for fraud protection (stable reference)
  const deviceId = useMemo(() => getDeviceId(), []);

  // Get checkout link from backend - only called on mount or explicit retry
  const initiateCheckout = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/payments/checkout-link', {
        packageId,
        scheduledOfferId,
        amount,
        packageTitle,
        origin: window.location.origin,
        deviceId: deviceId || undefined, // From nSure SDK for fraud protection
        allowedPaymentMethods, // Which payment methods to show in hosted checkout
        // Note: 3DS is handled internally by Coinflow's hosted checkout
        checkoutSessionId: checkoutSessionId || undefined, // Reuse existing checkout session if available
        // Promo code info for settlement
        promoCode: promoCode || undefined,
        promoBonusGc: promoBonusGC || 0,
        promoBonusSc: promoBonusSC || 0,
      });

      setCheckoutLink(response.link);
      setTransactionId(response.transactionId);
      // If we provided a checkoutSessionId, we're reusing an existing transaction
      ownsTransactionRef.current = !checkoutSessionId;
      
      // Check if this will be user's first purchase (for Meta Pixel tracking)
      try {
        const profileResponse = await apiClient.get('/profile');
        const profile = profileResponse.profile || profileResponse;
        setIsFirstPurchase(!profile.first_purchase_at);
      } catch {
        // Non-critical - default to false if profile fetch fails
      }
    } catch (err: any) {
      setError(err.message || 'Failed to initialize checkout. Please try again.');
      // Don't call onError here to prevent parent re-renders causing retries
      // The error state is shown in the UI with a "Try Again" button
    } finally {
      setIsLoading(false);
    }
  }, [packageId, scheduledOfferId, amount, packageTitle, deviceId, allowedPaymentMethods, checkoutSessionId]);

  // Only initiate checkout once on mount
  useEffect(() => {
    if (!hasInitiatedRef.current) {
      hasInitiatedRef.current = true;
      initiateCheckout();
    }
  }, []); // Empty dependency array - only run on mount

  // Handle postMessage events from Coinflow iframe
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // Only process messages from Coinflow
      if (!event.origin.includes('coinflow.cash')) {
        return;
      }

      let parsedData = event.data;
      if (typeof event.data === 'string') {
        try {
          parsedData = JSON.parse(event.data);
        } catch {
          // Not JSON, ignore
          return;
        }
      }

      const { method, data } = parsedData || {};

      switch (method) {
        case 'loaded':
          // Delay showing the iframe to allow it to fully render
          setTimeout(() => {
            setIframeLoaded(true);
          }, 1000);
          break;

        case 'success':
          // Payment completed successfully
          paymentCompletedRef.current = true; // Mark as completed to prevent abandon on unmount
          if (data?.paymentId) {
            onSuccess(data.paymentId, { isFirstPurchase });
          } else if (transactionId) {
            // If no paymentId in message, the webhook will handle it
            onSuccess(`transaction_${transactionId}`, { isFirstPurchase });
          }
          break;

        case 'close':
        case 'cancel':
          // User cancelled or closed the checkout
          // Mark the transaction as abandoned so we don't wait for the cron job
          // Only if we own the transaction (parent will handle cleanup if using shared session)
          if (transactionId && ownsTransactionRef.current) {
            apiClient.post('/payments/abandon-checkout', { transactionId }).catch(() => {});
          }
          if (onBack) {
            onBack();
          }
          break;

        case 'error':
          // Payment failed
          onError(new Error(data?.message || 'Payment failed'));
          break;
      }
    },
    [onSuccess, onError, onBack, transactionId]
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  // Cleanup: Mark transaction as abandoned if component unmounts without completion
  // This handles cases like user closing the modal by clicking outside or pressing escape
  useEffect(() => {
    return () => {
      // Only mark as abandoned if we have a transaction, payment wasn't completed, and we own the transaction
      if (transactionId && !paymentCompletedRef.current && ownsTransactionRef.current) {
        apiClient.post('/payments/abandon-checkout', { transactionId }).catch(() => {});
      }
    };
  }, [transactionId]);

  const handleIframeLoad = () => {
    if (iframeRef.current) {
      iframeRef.current.style.opacity = '1';
    }
  };

  // Handle manual retry - user clicked "Try Again"
  const handleRetry = useCallback(() => {
    initiateCheckout();
  }, [initiateCheckout]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-6 space-y-4">
        <div className="text-destructive text-center">
          <p className="font-semibold mb-2">Checkout Error</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
        <div className="flex gap-2">
          {onBack && (
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
          )}
          <Button onClick={handleRetry}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading || !checkoutLink) {
    return (
      <div className="flex flex-col items-center justify-center p-8 space-y-4 bg-background min-h-[300px]">
        {/* Gold Machine Logo */}
        <div className="relative">
          <div 
            className="absolute inset-0 blur-2xl opacity-30"
            style={{
              background: 'radial-gradient(circle, rgba(251,191,36,0.4) 0%, transparent 70%)',
              width: 160,
              height: 160,
              left: -40,
              top: -40,
            }}
          />
          <img
            src={logoSrc}
            alt="Gold Machine"
            width={80}
            height={80}
            className="relative z-10 animate-pulse object-contain"
            style={{ animationDuration: '2s' }}
          />
        </div>
        <p className="text-sm text-amber-400/80 font-medium">Loading secure checkout...</p>
        {/* Loading dots */}
        <div className="flex items-center gap-1">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-amber-500/60"
              style={{
                animation: 'dot-pulse 1.2s infinite ease-in-out',
                animationDelay: `${i * 0.15}s`,
              }}
            />
          ))}
        </div>
        <style>{`
          @keyframes dot-pulse {
            0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
            40% { opacity: 1; transform: scale(1); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="relative w-full min-h-[500px]">
      <iframe
        allow="payment;camera;clipboard-write"
        src={checkoutLink}
        onLoad={handleIframeLoad}
        ref={iframeRef}
        className="w-full border-none"
        style={{
          width: '100%',
          height: '100vh',
          maxHeight: '800px',
          minHeight: '500px',
          opacity: 0,
          transition: 'opacity 300ms linear',
        }}
        title="Coinflow Checkout"
      />

      {/* Loading overlay with Gold Machine logo */}
      {!iframeLoaded && (
        <div
          className={`
            absolute top-0 left-0 w-full h-full 
            bg-background
            flex flex-col items-center justify-center z-10
            transition-opacity duration-500 ease-out
            ${iframeLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100'}
          `}
        >
          <div className="relative mb-4">
            <div 
              className="absolute inset-0 blur-2xl opacity-30"
              style={{
                background: 'radial-gradient(circle, rgba(251,191,36,0.4) 0%, transparent 70%)',
                width: 120,
                height: 120,
                left: -30,
                top: -30,
              }}
            />
            <img
              src={logoSrc}
              alt="Gold Machine"
              width={60}
              height={60}
              className="relative z-10 animate-pulse object-contain"
              style={{ animationDuration: '2s' }}
            />
          </div>
          <div className="flex items-center gap-1">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="w-1.5 h-1.5 rounded-full bg-amber-500/60"
                style={{
                  animation: 'dot-pulse 1.2s infinite ease-in-out',
                  animationDelay: `${i * 0.15}s`,
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default CoinflowHostedCheckout;
